package com.arena_web.ui.components.cards

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.widgets.SlotBar
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

// =============================================================================
// Enums
// =============================================================================

enum class BookingField(val number: String, val accentColor: Color) {
    Field01("01", AppColors.Primary),                    // #0061FF blue
    Field02("02", SportColors.Tennis.secondary),          // #34D524 green
    Field03("03", SportColors.Basketball.secondary)       // #E88908 orange
}

enum class PaymentStatus(val label: String, val labelColor: Color) {
    Confirmed("Confirmed", SportColors.Tennis.primary),              // #7BFA6F
    Paid("Paid", SportColors.Padel.primary),                         // #3CFFFF
    Pending("Pending", SportColors.Basketball.metricsPrimary),       // #FFB84C
    Unpaid("Unpaid", Color.hex("FF4242"))                            // #FF4242
}

// =============================================================================
// BookingCard — filled variant
// =============================================================================

@Composable
fun BookingCard(
    field: BookingField,
    paymentStatus: PaymentStatus,
    customerName: String,
    timeFrom: String = "10:00",
    timeTo: String = "11:00",
    currency: String = "$",
    price: String = "100",
    modifier: Modifier = Modifier
) {
    val accentHex = "#${field.accentColor.toHexString()}"

    Column(
        modifier
            .sBorderRadius(12)
            .backgroundColor(BusinessColors.Booking.Background)
            .styleModifier {
                property("border-left", "${s(5)} solid $accentHex")
                property("border-top", "0.2px solid $accentHex")
                property("border-right", "0.2px solid $accentHex")
                property("border-bottom", "0.2px solid $accentHex")
                property("box-shadow", "0px ${s(4)} ${s(2)} rgba(0, 0, 0, 0.2)")
                property("padding", "${s(4)} ${s(10)} ${s(6)} ${s(14)}")
                property("overflow", "clip")
                property("gap", s(4))
            }
    ) {
        // Row 1: Customer name + optional indicator
        Row(
            Modifier
                .fillMaxWidth()
                .sMinHeight(22),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                customerName,
                modifier = Modifier
                    .bodyLSemiBold()
                    .color(AppColors.Text.Secondary)
                    .fillMaxWidth()
                    .styleModifier {
                        property("flex", "1 1 0")
                        property("min-width", "0")
                    }
            )
            when (paymentStatus) {
                PaymentStatus.Confirmed -> SlotBar(filled = false)
                PaymentStatus.Pending -> Image(
                    src = AppIcons.AlertExclamation,
                    modifier = Modifier.sWidth(2).sHeight(12),
                    alt = "Pending"
                )
                PaymentStatus.Unpaid -> Box(
                    Modifier.sSize(16).styleModifier { property("overflow", "clip") }
                ) {
                    Image(
                        src = AppIcons.DollarSign,
                        modifier = Modifier.fillMaxSize(),
                        alt = "Unpaid"
                    )
                }
                PaymentStatus.Paid -> { /* no indicator */ }
            }
        }

        // Row 2+3: Field info + status
        Column(
            Modifier.fillMaxWidth().styleModifier { property("gap", s(2)) }
        ) {
            // Field number + time range
            Row(
                Modifier.fillMaxWidth().styleModifier { property("gap", s(12)) },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    Modifier.styleModifier { property("gap", s(4)) }
                ) {
                    SpanText(
                        "Field",
                        modifier = Modifier.bodyMRegular().color(BusinessColors.Text.OnSurfaceMuted)
                    )
                    SpanText(
                        field.number,
                        modifier = Modifier.bodyMRegular().color(BusinessColors.Text.OnSurfaceMuted)
                    )
                }
                Row(
                    Modifier.styleModifier { property("gap", s(4)) }
                ) {
                    SpanText(
                        timeFrom,
                        modifier = Modifier.bodyMLight().color(BusinessColors.Text.OnSurfaceMuted)
                    )
                    SpanText(
                        "-",
                        modifier = Modifier.bodyMLight().color(BusinessColors.Text.OnSurfaceMuted)
                    )
                    SpanText(
                        timeTo,
                        modifier = Modifier.bodyMLight().color(BusinessColors.Text.OnSurfaceMuted)
                    )
                }
            }

            // Payment status label + price
            Row(
                Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    paymentStatus.label,
                    modifier = Modifier
                        .bodySMedium()
                        .color(paymentStatus.labelColor)
                        .styleModifier { property("flex", "1 1 0") }
                )
                Row(
                    Modifier.styleModifier { property("gap", s(2)) },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        currency,
                        modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                    )
                    SpanText(
                        price,
                        modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                    )
                }
            }
        }
    }
}

// =============================================================================
// BookingCardEmpty — empty slot
// =============================================================================

@Composable
fun BookingCardEmpty(
    modifier: Modifier = Modifier
) {
    Box(
        modifier
            .sHeight(70)
            .sBorderRadius(12)
            .styleModifier {
                property("border-left", "0.3px solid rgba(148, 149, 152, 0.3)")
                property("border-right", "0.3px solid rgba(148, 149, 152, 0.3)")
                property("border-top", "0.3px solid rgba(148, 149, 152, 0.3)")
                property("border-bottom", "none")
                property("overflow", "clip")
            }
    )
}
